"use client";

import { cjk } from "@streamdown/cjk";
import { code } from "@streamdown/code";
import type { Element, Root, Text } from "hast";
import { type ComponentProps, memo, useMemo } from "react";
import { harden } from "rehype-harden";
import { defaultRehypePlugins, Streamdown } from "streamdown";
import type { PluggableList, Plugin } from "unified";
import type { Parent } from "unist";
import { visit } from "unist-util-visit";
import { cn } from "@/lib/utils";
import {
  hasAutolinkSentenceBoundary,
  splitAutolinkAndSuffix,
} from "./response-autolink";

type ResponseProps = ComponentProps<typeof Streamdown>;

/** 日本語のリンク境界と表の空白を補正し、チャット本文を描画する。 */
export const Response = memo(
  ({ className, plugins, ...props }: ResponseProps) => {
    const mergedPlugins = useMemo(() => ({ cjk, code, ...plugins }), [plugins]);

    return (
      <div className="contents" data-ai-response="">
        <Streamdown
          className={cn(
            "!space-y-0 w-full max-w-[88ch] min-w-0 break-words text-sm leading-[1.6]",
            "whitespace-pre-wrap",
            "[&>*:first-child]:!mt-0 [&>*:last-child]:!mb-0",
            "[&_p]:!my-[0.7em]",
            "[&_h1]:!mt-[1.4em] [&_h1]:!mb-[0.6em] [&_h1]:font-semibold [&_h1]:text-lg [&_h1]:leading-[1.35]",
            "[&_h2]:!mt-[1.4em] [&_h2]:!mb-[0.6em] [&_h2]:font-semibold [&_h2]:text-base [&_h2]:leading-[1.35]",
            "[&_h3]:!mt-[1.4em] [&_h3]:!mb-[0.6em] [&_h3]:font-semibold [&_h3]:text-sm [&_h3]:leading-[1.35]",
            "[&_ul]:!my-2 [&_ul]:pl-5 [&_ol]:!my-2 [&_ol]:pl-5 [&_li]:my-0.5 [&_li>p]:!my-0 [&_li>ul]:!my-1 [&_li>ol]:!my-1 [&_li::marker]:text-muted-foreground",
            "[&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:decoration-primary/40 [&_a]:underline-offset-3 hover:[&_a]:decoration-primary",
            "[&_blockquote]:!my-4 [&_blockquote]:border-border [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground",
            "[&_code]:rounded-sm [&_code]:bg-muted/70 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[12px]",
            "[&_pre_code]:rounded-none [&_pre_code]:bg-transparent [&_pre_code]:p-0",
            "[&_pre]:!my-4 [&_pre]:max-h-[28rem] [&_pre]:overflow-auto [&_pre]:text-[12px] [&_pre]:leading-5",
            "[&_hr]:!my-5 [&_hr]:border-border/80",
            "[&_table]:text-[12px] [&_th]:bg-muted/45 [&_th]:px-2 [&_th]:py-1.5 [&_td]:px-2 [&_td]:py-1.5",
            "[&_[data-streamdown='code-block']]:!my-4 [&_[data-streamdown='table-wrapper']]:!my-4",
            className,
          )}
          plugins={mergedPlugins}
          rehypePlugins={responseRehypePlugins}
          {...props}
        />
      </div>
    );
  },
);

Response.displayName = "Response";

/**
 * Streamdownのテーブル直前に残る、空白のみのテキストノードを削除する。
 *
 * rehype実行時点ではtableはまだReactのラッパーへ変換されていないため、
 * HAST上のtable要素を対象にする。
 */
export const rehypeRemoveNewlinesBeforeTable: Plugin<[], Root> = () => {
  return (tree: Root) => {
    visit(
      tree,
      "element",
      (
        node: Element,
        index: number | undefined,
        parent: Parent | undefined,
      ) => {
        if (!parent || typeof index !== "number" || index === 0) {
          return;
        }

        const isTable = node.tagName === "table";
        const isTableWrapper =
          node.tagName === "div" &&
          node.properties?.["dataStreamdown"] === "table-wrapper";
        if (!isTable && !isTableWrapper) {
          return;
        }

        let tableIndex = index;
        while (tableIndex > 0) {
          const previousNode = parent.children[tableIndex - 1];
          if (
            previousNode?.type !== "text" ||
            (previousNode as Text).value.trim() !== ""
          ) {
            break;
          }

          parent.children.splice(tableIndex - 1, 1);
          tableIndex--;
        }
      },
    );
  };
};

/**
 * streamdown の自動リンク化で、句読点や続き文が URL に巻き込まれるケースを補正する。
 */
export const rehypeNormalizeAutolinkBoundaries: Plugin<[], Root> = () => {
  return (tree: Root) => {
    visit(
      tree,
      "element",
      (
        node: Element,
        index: number | undefined,
        parent: Parent | undefined,
      ) => {
        if (
          !parent ||
          typeof index !== "number" ||
          node.tagName !== "a" ||
          node.children.length !== 1
        ) {
          return;
        }

        const firstChild = node.children[0];
        if (!firstChild || firstChild.type !== "text") {
          return;
        }

        const href = node.properties?.href;
        const linkText = (firstChild as Text).value;
        if (typeof href !== "string") {
          return;
        }

        const decodedHref = decodeHrefIfNeeded(href);
        if (linkText !== href && linkText !== decodedHref) {
          return;
        }
        const isDecodedHrefMatch =
          linkText !== href && linkText === decodedHref;

        if (!hasAutolinkSentenceBoundary(linkText)) {
          return;
        }

        const { url, suffix } = splitAutolinkAndSuffix(linkText);
        if (!url || !suffix) {
          return;
        }

        // decodeURI による一致ケースのみ、文脈が続くときに限定して補正する。
        // これにより、正当なエンコードURLを段落末尾で誤って切断するのを防ぐ。
        if (
          isDecodedHrefMatch &&
          !hasSentenceContinuationAfterLink(parent, index) &&
          !hasSentenceContinuationInSuffix(suffix)
        ) {
          return;
        }

        node.properties.href = url;
        (firstChild as Text).value = url;
        const suffixNode = { type: "text", value: suffix } as Text;
        parent.children.splice(index + 1, 0, suffixNode);
      },
    );
  };
};

const SENTENCE_CONTINUATION_CHARS = /^[\s:：;；,，.。)\]】」』]/u;

function hasSentenceContinuationAfterLink(
  parent: Parent,
  index: number,
): boolean {
  const nextNode = parent.children[index + 1];
  if (!nextNode || nextNode.type !== "text") {
    return false;
  }

  return SENTENCE_CONTINUATION_CHARS.test((nextNode as Text).value);
}

const SENTENCE_SUFFIX_LEADING_CHARS = /^[\s:：;；,，.。)\]】」』]+/u;
const SENTENCE_SUFFIX_CONTEXT_CHARS = /[\s:：;；]/u;

function hasSentenceContinuationInSuffix(suffix: string): boolean {
  const sentenceCandidate = suffix.replace(SENTENCE_SUFFIX_LEADING_CHARS, "");
  if (!sentenceCandidate) {
    return false;
  }

  // URL 末尾の一部として妥当な記号で始まる場合は、段落末尾補正の対象外とする。
  if (/^[/?#&=]/u.test(sentenceCandidate)) {
    return false;
  }

  // 段落末尾補正は、明示的に文脈と判断できる記号がある場合に限定する。
  return SENTENCE_SUFFIX_CONTEXT_CHARS.test(sentenceCandidate);
}

function decodeHrefIfNeeded(href: string): string {
  try {
    return decodeURI(href);
  } catch {
    return href;
  }
}

// ストリームの各チャンクで Markdown 処理設定の参照を変えない。
const responseRehypePlugins = [
  defaultRehypePlugins.raw!,
  defaultRehypePlugins.sanitize!,
  // AI本文の画像URLへ会話情報を送らない。添付ファイルは別の表示経路を使う。
  [
    harden,
    {
      allowedImagePrefixes: [],
      allowDataImages: false,
      imageBlockPolicy: "text-only",
      allowedLinkPrefixes: ["*"],
      allowedProtocols: ["*"],
    } satisfies Parameters<typeof harden>[0],
  ],
  rehypeRemoveNewlinesBeforeTable,
  rehypeNormalizeAutolinkBoundaries,
] satisfies PluggableList;
